export interface Interval {
  semitones: number
  name: string
  abbreviation: string
}

export interface DifficultyTier {
  tier: number
  intervals: Interval[]
  choiceCount: number
}

const P1: Interval = { semitones: 0, name: 'Perfect Unison', abbreviation: 'P1' }
const m2: Interval = { semitones: 1, name: 'Minor Second', abbreviation: 'm2' }
const M2: Interval = { semitones: 2, name: 'Major Second', abbreviation: 'M2' }
const m3: Interval = { semitones: 3, name: 'Minor Third', abbreviation: 'm3' }
const M3: Interval = { semitones: 4, name: 'Major Third', abbreviation: 'M3' }
const P4: Interval = { semitones: 5, name: 'Perfect Fourth', abbreviation: 'P4' }
const TT: Interval = { semitones: 6, name: 'Tritone', abbreviation: 'TT' }
const P5: Interval = { semitones: 7, name: 'Perfect Fifth', abbreviation: 'P5' }
const m6: Interval = { semitones: 8, name: 'Minor Sixth', abbreviation: 'm6' }
const M6: Interval = { semitones: 9, name: 'Major Sixth', abbreviation: 'M6' }
const m7: Interval = { semitones: 10, name: 'Minor Seventh', abbreviation: 'm7' }
const M7: Interval = { semitones: 11, name: 'Major Seventh', abbreviation: 'M7' }
const P8: Interval = { semitones: 12, name: 'Perfect Octave', abbreviation: 'P8' }

export const ALL_INTERVALS: Interval[] = [P1, m2, M2, m3, M3, P4, TT, P5, m6, M6, m7, M7, P8]

export const DIFFICULTY_TIERS: DifficultyTier[] = [
  { tier: 1, intervals: [P1, P4, P5, P8], choiceCount: 2 },
  { tier: 2, intervals: [P1, m3, M3, P4, P5, P8], choiceCount: 3 },
  { tier: 3, intervals: [P1, m2, M2, m3, M3, P4, P5, m6, M6, P8], choiceCount: 4 },
  { tier: 4, intervals: [P1, m2, M2, m3, M3, P4, TT, P5, m6, M6, m7, M7, P8], choiceCount: 5 },
  { tier: 5, intervals: ALL_INTERVALS, choiceCount: 6 },
]

export const MAX_TIER = 5

/**
 * Clamps a game_difficulty level (nominally 1.0-5.0) to a whole tier. A missing or
 * non-numeric level reads as tier 1 rather than NaN, which would silently empty
 * every pool built from it.
 */
export function clampTier(level: number): number {
  if (!Number.isFinite(level)) return 1
  return Math.max(1, Math.min(MAX_TIER, Math.round(level)))
}

export function tierForLevel(musicDifficultyLevel: number): DifficultyTier {
  return DIFFICULTY_TIERS[clampTier(musicDifficultyLevel) - 1]
}

export type ClefPreference = 'TREBLE' | 'BASS' | 'BOTH'
export type IntervalType = 'melodic' | 'harmonic'

// MIDI note numbers: C4 = 60
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'] as const

// Comfortable ranges (MIDI numbers)
const TREBLE_MIN = 60 // C4
const TREBLE_MAX = 84 // C6
const BASS_MIN = 36  // C2
const BASS_MAX = 60  // C4

export interface GeneratedInterval {
  rootMidi: number
  secondMidi: number
  interval: Interval
  intervalType: IntervalType
  clef: 'treble' | 'bass'
  abc: string
  rootName: string
  secondName: string
}

export function midiToNoteName(midi: number): string {
  const octave = Math.floor(midi / 12) - 1
  const note = NOTE_NAMES[midi % 12]
  return `${note}${octave}`
}

// Convert MIDI note to ABC notation
export function midiToAbc(midi: number, clef: 'treble' | 'bass'): string {
  const octave = Math.floor(midi / 12) - 1
  const noteIdx = midi % 12

  // ABC note letters (using sharps with ^ prefix)
  const abcNotes = ['C', '^C', 'D', '^D', 'E', 'F', '^F', 'G', '^G', 'A', '^A', 'B']
  const baseLetter = abcNotes[noteIdx]

  // ABC octave encoding: C4 = C, C5 = c, C6 = c', C3 = C,, C2 = C,,
  if (octave >= 5) {
    const ticks = octave - 5
    return baseLetter.replace(/^(\^?)([A-G])/, (_, acc, n) => acc + n.toLowerCase()) + "'".repeat(ticks)
  } else if (octave === 4) {
    return baseLetter
  } else {
    const commas = 4 - octave
    return baseLetter + ','.repeat(commas)
  }
}

function randomInt(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min
}

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]
}

export function generateInterval(
  tier: DifficultyTier,
  clefPreference: ClefPreference,
  intervalType: IntervalType,
): GeneratedInterval {
  const clef: 'treble' | 'bass' =
    clefPreference === 'BOTH'
      ? pickRandom(['treble', 'bass'])
      : clefPreference === 'BASS'
        ? 'bass'
        : 'treble'

  const min = clef === 'treble' ? TREBLE_MIN : BASS_MIN
  const max = clef === 'treble' ? TREBLE_MAX : BASS_MAX

  const interval = pickRandom(tier.intervals)
  const rootMax = max - interval.semitones
  const rootMidi = randomInt(min, Math.max(min, rootMax))
  const secondMidi = rootMidi + interval.semitones

  const rootAbc = midiToAbc(rootMidi, clef)
  const secondAbc = midiToAbc(secondMidi, clef)

  const clefAbc = clef === 'bass' ? 'bass' : 'treble'
  let abc: string
  if (intervalType === 'harmonic') {
    abc = `X:1\nK:C clef=${clefAbc}\nL:1/1\n[${rootAbc}${secondAbc}]|\n`
  } else {
    abc = `X:1\nK:C clef=${clefAbc}\nL:1/2\n${rootAbc} ${secondAbc}|\n`
  }

  return {
    rootMidi,
    secondMidi,
    interval,
    intervalType,
    clef,
    abc,
    rootName: midiToNoteName(rootMidi),
    secondName: midiToNoteName(secondMidi),
  }
}

export function generateDistractors(
  correct: Interval,
  tier: DifficultyTier,
  count: number,
): Interval[] {
  const pool = tier.intervals.filter((i) => i.abbreviation !== correct.abbreviation)
  const shuffled = [...pool].sort(() => Math.random() - 0.5)
  return shuffled.slice(0, count)
}

export function generateChoices(correct: Interval, tier: DifficultyTier): Interval[] {
  const distractors = generateDistractors(correct, tier, tier.choiceCount - 1)
  const choices = [correct, ...distractors]
  return choices.sort(() => Math.random() - 0.5)
}
